import React, { useState, useCallback } from 'react';
import cc from 'classcat';
import AnimateHeight from 'react-animate-height';

import FilterIcon from '../../images/icons/new-caret-down.svg';
import SearchIcon from '../../images/icons/search.svg';

import styles from './styles.module.scss';

export type SortOption = 'featured' | 'name-asc' | 'name-desc' | 'distance';

export interface VillageFilterState {
  type: 'sale' | 'rent' | null;
  availability: 'all' | 'available' | null;
  areas: string[];
  postcode: string;
  sortBy: SortOption;
}

interface Props {
  onFilterChange: (filters: VillageFilterState) => void;
  resultCount: number;
  totalCount: number;
  sortedByDistance: boolean;
}

const AREA_OPTIONS = [
  { value: 'north', label: 'North of the River' },
  { value: 'south', label: 'South of the River' },
  { value: 'western', label: 'Western Suburbs' },
  { value: 'eastern', label: 'Eastern Suburbs' },
  { value: 'regional', label: 'Regional' },
];

const VillageFilters = ({ onFilterChange, resultCount, totalCount, sortedByDistance }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<VillageFilterState>({
    type: null,
    availability: 'all',
    areas: [],
    postcode: '',
    sortBy: 'featured',
  });

  const updateFilters = useCallback(
    (newFilters: Partial<VillageFilterState>) => {
      const updated = { ...filters, ...newFilters };
      setFilters(updated);
      onFilterChange(updated);
    },
    [filters, onFilterChange],
  );

  const handleTypeChange = (type: 'sale' | 'rent' | null) => {
    updateFilters({ type });
  };

  const handleAvailabilityChange = (availability: 'all' | 'available') => {
    updateFilters({ availability });
  };

  const handleAreaToggle = (area: string) => {
    const areas = filters.areas.includes(area)
      ? filters.areas.filter((a) => a !== area)
      : [...filters.areas, area];
    updateFilters({ areas });
  };

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    const postcode = value.replace(/[^0-9]/g, '');

    // Update local state immediately for display
    setFilters({ ...filters, postcode });

    // Only trigger filtering when we have 4 digits or field is cleared
    if (postcode.length === 4 || postcode.length === 0) {
      onFilterChange({ ...filters, postcode });
    }
  };

  const handlePostcodeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Form submit still works but not required since it's dynamic
  };

  const clearType = () => updateFilters({ type: null });
  const clearAvailability = () => updateFilters({ availability: 'all' });
  const clearAreas = () => updateFilters({ areas: [] });
  const clearAll = () => {
    const cleared: VillageFilterState = {
      type: null,
      availability: 'all',
      areas: [],
      postcode: '',
      sortBy: 'featured',
    };
    setFilters(cleared);
    onFilterChange(cleared);
    setIsOpen(false); // Close the filter panel
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sortBy = e.target.value as SortOption;
    updateFilters({ sortBy });
  };

  const hasActiveFilters = filters.type || filters.availability !== 'all' || filters.areas.length > 0 || filters.postcode;

  return (
    <div className={styles.villageFilters}>
      <div className={styles.topBar}>
        <form className={styles.searchForm} onSubmit={handlePostcodeSubmit}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={filters.postcode}
            placeholder="Nearest to postcode"
            onChange={handlePostcodeChange}
          />
          <button type="submit" aria-label="Search">
            <SearchIcon />
          </button>
        </form>

        <button
          type="button"
          className={cc([styles.filterButton, { [styles.active]: isOpen }])}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FilterIcon />
          Filters
        </button>

        <button
          type="button"
          className={styles.clearFiltersButton}
          onClick={clearAll}
          title="Clear all filters"
          disabled={!hasActiveFilters}
        >
          ✕
        </button>

        <div className={styles.resultCount}>
          Showing {resultCount} of {totalCount}
        </div>

        <div className={styles.sortBy}>
          Sort by{' '}
          {sortedByDistance ? (
            <strong>Distance</strong>
          ) : (
            <select value={filters.sortBy} onChange={handleSortChange}>
              <option value="featured">Featured</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
            </select>
          )}
        </div>
      </div>

      <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
        <div className={styles.filterPanel}>
          <h3 className={styles.filterTitle}>Filters</h3>

          {/* Availability Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterHeader}>
              <h4>Availability</h4>
              {filters.availability !== 'all' && (
                <button type="button" className={styles.clearButton} onClick={clearAvailability}>
                  Clear
                </button>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={cc([styles.toggleButton, { [styles.active]: filters.availability === 'all' }])}
                onClick={() => handleAvailabilityChange('all')}
              >
                All
              </button>
              <button
                type="button"
                className={cc([styles.toggleButton, { [styles.active]: filters.availability === 'available' }])}
                onClick={() => handleAvailabilityChange('available')}
              >
                Has Available Units
              </button>
            </div>
          </div>

          {/* Type Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterHeader}>
              <h4>Type</h4>
              {filters.type && (
                <button type="button" className={styles.clearButton} onClick={clearType}>
                  Clear
                </button>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={cc([styles.toggleButton, { [styles.active]: filters.type === null }])}
                onClick={() => handleTypeChange(null)}
              >
                All
              </button>
              <button
                type="button"
                className={cc([styles.toggleButton, { [styles.active]: filters.type === 'sale' }])}
                onClick={() => handleTypeChange('sale')}
              >
                For Sale
              </button>
              <button
                type="button"
                className={cc([styles.toggleButton, { [styles.active]: filters.type === 'rent' }])}
                onClick={() => handleTypeChange('rent')}
              >
                For Rent
              </button>
            </div>
          </div>

          {/* Area Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterHeader}>
              <h4>Area</h4>
              {filters.areas.length > 0 && (
                <button type="button" className={styles.clearButton} onClick={clearAreas}>
                  Clear
                </button>
              )}
            </div>
            <div className={styles.checkboxGroup}>
              {AREA_OPTIONS.map((option) => (
                <label key={option.value} className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={filters.areas.includes(option.value)}
                    onChange={() => handleAreaToggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.clearAllButton}
              onClick={clearAll}
              disabled={!hasActiveFilters}
            >
              Clear all
            </button>
            <button type="button" className={styles.applyButton} onClick={() => setIsOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
};

export default VillageFilters;

